import { World, Body, Vec2, Circle, Box, Polygon, RevoluteJoint, type Contact, type Fixture } from 'planck';
import {
  BALL, BALL_R, BOUNCY, CAT, CONVEYOR_SPEED, DYNAMIC, GRAVITY, LOST_AFTER, OBSTACLE, POSITION_ITER, STEP_DT, STROKE, STROKE_T,
  VELOCITY_ITER, WORLD_H, WORLD_W,
} from './constants';
import { toWorld, triangulate, type Level, type Pt, type Rect } from './level';
import { buildSegments, isBlocked, truncateAtBlock, type Blocker, type StrokeSegment } from './stroke';

export type Status = 'playing' | 'cleared' | 'lost';

export type StrokeBody = { body: Body; segment: StrokeSegment; /** body 로컬 좌표의 점열(선) 또는 [0,0](점) */ local: Pt[] };
export type DynamicBody = { body: Body; shape: { pts: Pt[] } | { r: number }; pin?: Pt };
/** 월드 좌표 사각형(중심·반폭·반높이). */
export type WRect = { cx: number; cy: number; hw: number; hh: number };

export type Snapshot = {
  status: Status;
  balls: { blue: Pt; pink: Pt; r: number };
  strokes: { pos: Pt; angle: number; segment: StrokeSegment; local: Pt[] }[];
  obstacles: Pt[][];
  holes: Pt[][][];
  zones: Pt[][];
  bouncy: { a: Pt; b: Pt }[];
  dynamics: { pos: Pt; angle: number; shape: { pts: Pt[] } | { r: number }; pin?: Pt }[];
  ghosts: WRect[];
  conveyors: (WRect & { dir: 1 | -1; angle: number })[];
  ice: (WRect & { alive: boolean })[];
  time: number;
};

type Tag =
  | { kind: 'ball'; name: 'blue' | 'pink' }
  | { kind: 'stroke' }
  | { kind: 'conveyor'; dir: 1 | -1; speed: number }
  | { kind: 'ice'; index: number }
  | { kind: 'static' }
  | { kind: 'dynamic' }
  | { kind: 'ghost' };

function rectW(r: Rect): WRect {
  const [x0, y0] = toWorld([r.x, r.y]);
  const [x1, y1] = toWorld([r.x + r.w, r.y + r.h]);
  return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, hw: Math.abs(x1 - x0) / 2, hh: Math.abs(y1 - y0) / 2 };
}
function rectPoly(r: WRect, angle = 0): Pt[] {
  const c = Math.cos(angle), s = Math.sin(angle);
  const corners: Pt[] = [[-r.hw, -r.hh], [r.hw, -r.hh], [r.hw, r.hh], [-r.hw, r.hh]];
  return corners.map(([x, y]) => [r.cx + x * c - y * s, r.cy + x * s + y * c]);
}

/** 한 판의 물리 세계. 좌표는 전부 월드(m, y 위로). */
export class GameWorld {
  readonly world: World;
  readonly level: Level;
  readonly obstacles: Pt[][];
  readonly holes: Pt[][][];
  readonly zones: Pt[][];
  readonly bouncy: { a: Pt; b: Pt }[];
  readonly ghosts: WRect[];
  readonly conveyors: (WRect & { dir: 1 | -1; angle: number })[];
  readonly ice: { rect: WRect; body: Body; alive: boolean }[] = [];
  readonly dynamics: DynamicBody[] = [];
  readonly ballR: number;
  blue: Body;
  pink: Body;
  strokes: StrokeBody[] = [];
  status: Status = 'playing';
  /** 원작: 첫 선을 놓기 전까지 물리는 멈춰 있다(공이 공중에 떠 있음). */
  started = false;
  time = 0;
  private outSince: number | null = null;
  private meltQueue: number[] = [];

  constructor(level: Level) {
    this.level = level;
    this.world = new World({ gravity: new Vec2(0, GRAVITY), allowSleep: true });
    this.ballR = level.balls.r * WORLD_H;
    this.obstacles = level.obstacles.map((o) => o.pts.map(toWorld));
    this.holes = level.obstacles.map((o) => (o.holes ?? []).map((h) => h.map(toWorld)));
    this.zones = (level.zones ?? []).filter((z) => z.kind === 'nodraw').map((z) => z.pts.map(toWorld));
    this.bouncy = (level.bouncy ?? []).map((b) => ({ a: toWorld(b.a), b: toWorld(b.b) }));
    this.ghosts = (level.ghosts ?? []).map(rectW);
    this.conveyors = (level.conveyors ?? []).map((c) => {
      const [cx, cy] = toWorld([c.cx, c.cy]);
      // 화면 각도(y 아래로)는 월드(y 위로)에서 부호가 뒤집힌다
      return { cx, cy, hw: (c.w * WORLD_H) / 2, hh: (c.h * WORLD_H) / 2, dir: c.dir, angle: -((c.angle ?? 0) * Math.PI) / 180 };
    });

    const ground = this.world.createBody({ type: 'static' });
    const staticTag: Tag = { kind: 'static' };
    this.obstacles.forEach((poly, oi) => {
      const holes = (level.obstacles[oi]?.holes ?? []).map((h) => h.map(toWorld));
      if (poly.length === 4 && isAxisRect(poly) && !holes.length) {
        const xs = poly.map((p) => p[0]), ys = poly.map((p) => p[1]);
        const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
        ground.createFixture(new Box((maxX - minX) / 2, (maxY - minY) / 2, new Vec2((minX + maxX) / 2, (minY + maxY) / 2)), { friction: OBSTACLE.friction, userData: staticTag });
      } else {
        for (const tri of triangulate(poly, holes)) {
          ground.createFixture(new Polygon(tri.map(([x, y]) => new Vec2(x, y))), { friction: OBSTACLE.friction, userData: staticTag });
        }
      }
    });
    for (const { a, b } of this.bouncy) {
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (len < 1e-6) continue;
      const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
      ground.createFixture(new Box(len / 2, BOUNCY.thickness / 2, new Vec2((a[0] + b[0]) / 2, (a[1] + b[1]) / 2), ang), { friction: BOUNCY.friction, restitution: BOUNCY.restitution, userData: staticTag });
    }
    for (const g of this.ghosts) {
      // 공은 통과, 선·움직이는 물체만 걸린다
      ground.createFixture(new Box(g.hw, g.hh, new Vec2(g.cx, g.cy)), {
        friction: OBSTACLE.friction, filterCategoryBits: CAT.GHOST, filterMaskBits: CAT.STROKE | CAT.DYNAMIC, userData: { kind: 'ghost' } as Tag,
      });
    }
    this.conveyors.forEach((c, i) => {
      const speed = level.conveyors?.[i]?.speed ?? CONVEYOR_SPEED;
      ground.createFixture(new Box(c.hw, c.hh, new Vec2(c.cx, c.cy), c.angle), { friction: 0.8, userData: { kind: 'conveyor', dir: c.dir, speed } as Tag });
    });
    (level.ice ?? []).forEach((r, index) => {
      const rect = rectW(r);
      const body = this.world.createBody({ type: 'static' });
      body.createFixture(new Box(rect.hw, rect.hh, new Vec2(rect.cx, rect.cy)), { friction: OBSTACLE.friction, filterCategoryBits: CAT.ICE, userData: { kind: 'ice', index } as Tag });
      this.ice.push({ rect, body, alive: true });
    });
    for (const d of level.dynamics ?? []) {
      if ('circle' in d) {
        const c = toWorld([d.circle[0], d.circle[1]]);
        const r = d.circle[2] * WORLD_H;
        const body = this.world.createBody({ type: 'dynamic', position: new Vec2(c[0], c[1]), bullet: true });
        body.createFixture(new Circle(r), { ...DYNAMIC, filterCategoryBits: CAT.DYNAMIC, userData: { kind: 'dynamic' } as Tag });
        this.dynamics.push({ body, shape: { r }, pin: d.pin ? toWorld(d.pin) : undefined });
        if (d.pin) this.pin(body, d.pin, ground);
      } else {
        const pts = d.pts.map(toWorld);
        const cx = pts.reduce((a, p) => a + p[0], 0) / pts.length;
        const cy = pts.reduce((a, p) => a + p[1], 0) / pts.length;
        const local: Pt[] = pts.map(([x, y]) => [x - cx, y - cy]);
        const body = this.world.createBody({ type: 'dynamic', position: new Vec2(cx, cy), bullet: true });
        for (const tri of triangulate(local)) {
          body.createFixture(new Polygon(tri.map(([x, y]) => new Vec2(x, y))), { ...DYNAMIC, filterCategoryBits: CAT.DYNAMIC, userData: { kind: 'dynamic' } as Tag });
        }
        this.dynamics.push({ body, shape: { pts: local }, pin: d.pin ? toWorld(d.pin) : undefined });
        if (d.pin) this.pin(body, d.pin, ground);
      }
    }

    this.blue = this.makeBall(toWorld(level.balls.blue), 'blue');
    this.pink = this.makeBall(toWorld(level.balls.pink), 'pink');
    // 트레이싱 오차로 공이 바닥에서 살짝 떠 있으면 바로 아래 면에 붙인다(첫 선 전엔 물리가 멈춰 있어 눈에 띈다)
    for (const b of [this.blue, this.pink]) this.snapDown(b);

    this.world.on('begin-contact', (c: Contact) => this.onContact(c));
    this.world.on('pre-solve', (c: Contact) => this.onPreSolve(c));
  }

  /** 공 바로 아래(반지름 + 0.5m 안)에 면이 있으면 그 위에 딱 붙인다. */
  private snapDown(ball: Body) {
    const p = ball.getPosition();
    const from = new Vec2(p.x, p.y), to = new Vec2(p.x, p.y - this.ballR - 0.5);
    let best: number | null = null;
    this.world.rayCast(from, to, (fixture, point, _normal, fraction) => {
      if (fixture.getBody() === ball) return -1;
      const t = this.tag(fixture);
      if (t?.kind === 'ball') return -1;
      if (best === null || point.y > best) best = point.y;
      return fraction; // 더 가까운 것만
    });
    if (best !== null && best + this.ballR < p.y) ball.setPosition(new Vec2(p.x, best + this.ballR + 0.005));
  }

  /** 풍차 축: 월드 고정점에 회전 관절. */
  private pin(body: Body, at: Pt, ground: Body) {
    const w = toWorld(at);
    this.world.createJoint(new RevoluteJoint({}, ground, body, new Vec2(w[0], w[1])));
  }

  private makeBall(p: Pt, name: 'blue' | 'pink'): Body {
    const b = this.world.createBody({ type: 'dynamic', position: new Vec2(p[0], p[1]), bullet: true, userData: name });
    b.createFixture(new Circle(this.ballR), { ...BALL, filterCategoryBits: CAT.BALL, userData: { kind: 'ball', name } as Tag });
    return b;
  }

  private tag(f: Fixture): Tag | undefined {
    return f.getUserData() as Tag | undefined;
  }

  private onContact(c: Contact) {
    if (this.status !== 'playing') return;
    const fa = c.getFixtureA(), fb = c.getFixtureB();
    const a = fa.getBody(), b = fb.getBody();
    if ((a === this.blue && b === this.pink) || (a === this.pink && b === this.blue)) { this.status = 'cleared'; return; }
    // 얼음: 선이 닿으면 녹는다
    const ta = this.tag(fa), tb = this.tag(fb);
    if (ta?.kind === 'ice' && tb?.kind === 'stroke') this.meltQueue.push(ta.index);
    else if (tb?.kind === 'ice' && ta?.kind === 'stroke') this.meltQueue.push(tb.index);
  }

  private onPreSolve(c: Contact) {
    const ta = this.tag(c.getFixtureA()), tb = this.tag(c.getFixtureB());
    // 컨베이어: 접촉면의 접선 속도. 법선은 A→B 이므로 A 가 컨베이어면 +, B 면 −.
    if (ta?.kind === 'conveyor') c.setTangentSpeed(ta.dir * ta.speed);
    else if (tb?.kind === 'conveyor') c.setTangentSpeed(-tb.dir * tb.speed);
  }

  /** 지금 시점의 차단 영역(장애물·그물·움직이는 물체·바·얼음 + 공). 그리는 중 실시간 판정에 쓴다. */
  blockers(): Blocker[] {
    const out: Blocker[] = this.obstacles.map((pts) => ({ kind: 'poly' as const, pts }));
    for (const pts of this.zones) out.push({ kind: 'poly', pts });
    for (const g of this.ghosts) out.push({ kind: 'poly', pts: rectPoly(g) });
    for (const c of this.conveyors) out.push({ kind: 'poly', pts: rectPoly(c, c.angle) });
    for (const i of this.ice) if (i.alive) out.push({ kind: 'poly', pts: rectPoly(i.rect) });
    for (const d of this.dynamics) {
      const p = d.body.getPosition(), ang = d.body.getAngle();
      if ('r' in d.shape) out.push({ kind: 'circle', c: [p.x, p.y], r: d.shape.r });
      else {
        const cos = Math.cos(ang), sin = Math.sin(ang);
        out.push({ kind: 'poly', pts: d.shape.pts.map(([x, y]) => [p.x + x * cos - y * sin, p.y + x * sin + y * cos]) });
      }
    }
    for (const b of [this.blue, this.pink]) {
      const p = b.getPosition();
      out.push({ kind: 'circle', c: [p.x, p.y], r: this.ballR });
    }
    return out;
  }

  /** 이미 놓인 선들(현재 위치). 새 선이 이걸 지나면 전체 취소. */
  strokeBlockers(): Blocker[] {
    return this.strokes.map((s) => {
      const p = s.body.getPosition(), ang = s.body.getAngle();
      const cos = Math.cos(ang), sin = Math.sin(ang);
      return { kind: 'path' as const, r: STROKE_T / 2, pts: s.local.map(([x, y]) => [p.x + x * cos - y * sin, p.y + x * sin + y * cos] as Pt) };
    });
  }

  /** 손을 뗀 점열(월드 좌표)을 강체로. 만들어진 조각 수를 돌려준다.
   *  규칙(사용자 확인): 공·장애물에 닿으면 그 앞까지만(거기서 손을 뗀 것처럼), 이미 놓인 선을 지나면 취소. 자기 교차는 허용. */
  addStroke(raw: Pt[]): number {
    if (this.status !== 'playing') return 0;
    const blockers = this.blockers();
    const cut = truncateAtBlock(raw, blockers);
    if (!cut.length) return 0;   // 자기 교차는 허용(사용자 요청: 동글동글 말기)
    const existing = this.strokeBlockers();
    if (existing.length && cut.some((p) => isBlocked(p, existing, STROKE_T / 2))) return 0;
    const segs = buildSegments(cut, blockers);
    for (const seg of segs) this.strokes.push(this.makeStrokeBody(seg));
    if (segs.length) this.started = true;
    return segs.length;
  }

  private makeStrokeBody(seg: StrokeSegment): StrokeBody {
    const half = STROKE_T / 2;
    const opt = { ...STROKE, filterCategoryBits: CAT.STROKE, userData: { kind: 'stroke' } as Tag };
    if (seg.kind === 'dot') {
      const body = this.world.createBody({ type: 'dynamic', position: new Vec2(seg.c[0], seg.c[1]), bullet: true });
      body.createFixture(new Circle(half), opt);
      return { body, segment: seg, local: [[0, 0]] };
    }
    const cx = seg.pts.reduce((a, p) => a + p[0], 0) / seg.pts.length;
    const cy = seg.pts.reduce((a, p) => a + p[1], 0) / seg.pts.length;
    const local: Pt[] = seg.pts.map(([x, y]) => [x - cx, y - cy]);
    const body = this.world.createBody({ type: 'dynamic', position: new Vec2(cx, cy), bullet: true });
    for (let i = 0; i < local.length; i++) {
      const p = local[i]!;
      body.createFixture(new Circle(new Vec2(p[0], p[1]), half), opt);
      if (i + 1 < local.length) {
        const q = local[i + 1]!;
        const len = Math.hypot(q[0] - p[0], q[1] - p[1]);
        if (len < 1e-6) continue;
        const ang = Math.atan2(q[1] - p[1], q[0] - p[0]);
        body.createFixture(new Box(len / 2, half, new Vec2((p[0] + q[0]) / 2, (p[1] + q[1]) / 2), ang), opt);
      }
    }
    return { body, segment: seg, local };
  }

  step(dt = STEP_DT) {
    if (this.status !== 'playing' || !this.started) return;
    this.world.step(dt, VELOCITY_ITER, POSITION_ITER);
    this.time += dt;
    if (this.meltQueue.length) {
      for (const i of this.meltQueue) {
        const ice = this.ice[i];
        if (ice && ice.alive) { ice.alive = false; this.world.destroyBody(ice.body); }
      }
      this.meltQueue = [];
    }
    const out = [this.blue, this.pink].some((b) => {
      const p = b.getPosition();
      const m = 2 * this.ballR;
      return p.x < -m || p.x > WORLD_W + m || p.y < -m || p.y > WORLD_H + m;
    });
    if (out) {
      if (this.outSince === null) this.outSince = this.time;
      else if (this.time - this.outSince >= LOST_AFTER) this.status = 'lost';
    } else this.outSince = null;
  }

  snapshot(): Snapshot {
    const bp = this.blue.getPosition(), pp = this.pink.getPosition();
    return {
      status: this.status,
      time: this.time,
      balls: { blue: [bp.x, bp.y], pink: [pp.x, pp.y], r: this.ballR },
      obstacles: this.obstacles,
      holes: this.holes,
      zones: this.zones,
      bouncy: this.bouncy,
      ghosts: this.ghosts,
      conveyors: this.conveyors,
      ice: this.ice.map((i) => ({ ...i.rect, alive: i.alive })),
      dynamics: this.dynamics.map((d) => { const p = d.body.getPosition(); return { pos: [p.x, p.y], angle: d.body.getAngle(), shape: d.shape, pin: d.pin }; }),
      strokes: this.strokes.map((s) => {
        const p = s.body.getPosition();
        return { pos: [p.x, p.y], angle: s.body.getAngle(), segment: s.segment, local: s.local };
      }),
    };
  }
}

function isAxisRect(p: Pt[]): boolean {
  const eps = 1e-6;
  for (let i = 0; i < 4; i++) {
    const a = p[i]!, b = p[(i + 1) % 4]!;
    if (Math.abs(a[0] - b[0]) > eps && Math.abs(a[1] - b[1]) > eps) return false;
  }
  return true;
}

export { BALL_R };
